import type { Dispatch, SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useNotifications from '../../../_lib/notifications/useNotifications';
import { deleteMessages, saveDraft } from '../../../api/cmail.api';
import { useAPIErrorHandler } from '../../../components/hooks/useAPIErrorHandler';
import { queries } from '../../../queries';
import {
  getMessageId,
  getReplyToMsgId,
  normalizeMessageCategoryForAPI,
  shouldIncludeMessageCategory,
} from '../utils/emailTypeUtils';
import { ensureHtmlDocument } from '../utils/htmlContentUtils';
import type { CleanedFormData } from '../utils/formValidationUtils';
import type { FormState } from './useEmailForm';

export interface UseDraftManagementProps {
  readonly form: FormState;
  readonly message?: any;
  readonly mode?: 'new' | 'reply' | 'reply-single' | 'forward';
  readonly currentDraftMsgId: string | null;
  readonly setCurrentDraftMsgId: Dispatch<SetStateAction<string | null>>;
  readonly selectedFromEmail: string;
  /** The name that goes with the From address; see `resolveSenderName`. */
  readonly getSenderName: () => string;
  readonly msgCategory?: string;
  readonly getEmailType: () => 'NEW' | 'REPLY' | 'FORWARD' | 'FOLLOWUP';
  readonly getReferences: () => string[];
  readonly cleanFormData: (formData: FormState) => CleanedFormData;
  readonly hasUnsavedChanges: () => boolean;
}

export const useDraftManagement = ({
  form,
  message,
  mode = 'new',
  currentDraftMsgId,
  setCurrentDraftMsgId,
  selectedFromEmail,
  getSenderName,
  msgCategory,
  getEmailType,
  getReferences,
  cleanFormData: cleanFormDataProp,
  hasUnsavedChanges,
}: UseDraftManagementProps) => {
  const handleAPIError = useAPIErrorHandler();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const saveDraftMessage = async (formData: FormState, msgId?: string) => {
    try {
      const cleanedForm = cleanFormDataProp(formData);

      // Don't pass msgCategory for OTHER or Other categories
      const shouldIncludeMsgCategory =
        shouldIncludeMessageCategory(msgCategory);

      const draftResponse = await saveDraft({
        data: {
          to: cleanedForm.to,
          cc: cleanedForm.cc,
          bcc: cleanedForm.bcc,
          subject: cleanedForm.subject,
          body: cleanedForm.body,
          htmlBody: ensureHtmlDocument(cleanedForm.body),
          attachments: cleanedForm.attachments.map((attachment) => ({
            docId: attachment.docId,
            docName: attachment.docName,
            docContentType: attachment.docContentType,
            docSize: attachment.docSize,
          })),
          from: selectedFromEmail,
          senderName: getSenderName(),
          msgId: getMessageId(message, mode, msgId || currentDraftMsgId),
          emailType: getEmailType(),
          replyToMsgId: getReplyToMsgId(message, mode),
          references: getReferences().length > 0 ? getReferences() : undefined,
          ...(shouldIncludeMsgCategory && {
            msgCategory: normalizeMessageCategoryForAPI(msgCategory),
          }),
        },
      });

      // Extract msgId from the response. Use functional update so we don't overwrite
      // if another flow (e.g. attachment upload) already set currentDraftMsgId.
      const newMsgId = draftResponse.data.msgId;
      if (newMsgId) {
        setCurrentDraftMsgId((prev) => prev ?? newMsgId);
      }

      return true;
    } catch (error: any) {
      handleAPIError('Failed to save draft')(error);
      return false;
    }
  };

  const deleteDraftMessage = async () => {
    if (!currentDraftMsgId) {
      return true; // No draft to delete
    }

    try {
      // Call the delete messages API to delete the draft
      await deleteMessages({
        data: [currentDraftMsgId],
      });

      // Clear local state after successful deletion
      setCurrentDraftMsgId(null);
      return true;
    } catch (error: any) {
      handleAPIError('Failed to delete draft')(error);
      return false;
    }
  };

  const handleDelete = async () => {
    const success = await deleteDraftMessage();
    if (success) {
      notifications.success('Draft deleted successfully');
      // Use the correct query invalidation pattern
      queryClient.invalidateQueries(queries.cmail.list._def);
      // Invalidate draft stats to refresh the badge count
      queryClient.invalidateQueries(['draft-stats']);
    }
    return success;
  };

  const handleClose = async () => {
    if (hasUnsavedChanges()) {
      // Save draft before closing
      const success = await saveDraftMessage(form);
      if (success) {
        // Use the correct query invalidation pattern
        queryClient.invalidateQueries(queries.cmail.list._def);
        // Invalidate draft stats to refresh the badge count
        queryClient.invalidateQueries(['draft-stats']);
      }
    }
  };

  // No auto-save functionality - drafts are only saved when closing the modal

  return {
    saveDraftMessage,
    deleteDraftMessage,
    handleDelete,
    handleClose,
  };
};
